import { maybeSignedIn } from '../utils/session';
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const ThemeContext = createContext();

function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** API uses `system`; UI uses `auto` for follow-OS. */
export function normalizeUserTheme(raw) {
  if (raw === 'light' || raw === 'dark') {
    return raw;
  }
  if (raw === 'system' || raw === 'auto') {
    return 'auto';
  }
  return 'auto';
}

/** Persist to API: UI `auto` maps to backend `system`. */
function toApiThemePreference(uiTheme) {
  return uiTheme === 'auto' ? 'system' : uiTheme;
}

function getInitialEffectiveTheme() {
  try {
    const saved = normalizeUserTheme(localStorage.getItem('theme') || 'auto');
    if (saved === 'auto') {
      return getSystemTheme();
    }
    return saved;
  } catch {
    /* ignore */
  }
  return getSystemTheme();
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => normalizeUserTheme(localStorage.getItem('theme') || 'auto'));
  const [effectiveTheme, setEffectiveTheme] = useState(getInitialEffectiveTheme);
  const [loading, setLoading] = useState(true);

  // Load theme preference from user settings
  useEffect(() => {
    const loadTheme = async () => {
      try {
        if (maybeSignedIn()) {
          const response = await api.get('/core/me');
          const userTheme = response.data.user?.theme_preference || 'auto';
          setTheme(normalizeUserTheme(userTheme));
        } else {
          const savedTheme = localStorage.getItem('theme') || 'auto';
          setTheme(normalizeUserTheme(savedTheme));
        }
      } catch (error) {
        console.error('Failed to load theme:', error);
        const savedTheme = localStorage.getItem('theme') || 'auto';
        setTheme(normalizeUserTheme(savedTheme));
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, []);

  // Calculate effective theme: data-theme is always `light` or `dark`
  useEffect(() => {
    const calculateEffectiveTheme = () => {
      if (theme === 'auto' || theme === 'system') {
        return getSystemTheme();
      }
      if (theme === 'light' || theme === 'dark') {
        return theme;
      }
      return getSystemTheme();
    };

    const newEffectiveTheme = calculateEffectiveTheme();
    setEffectiveTheme(newEffectiveTheme);

    document.documentElement.setAttribute('data-theme', newEffectiveTheme);
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${newEffectiveTheme}`);

    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        const next = e.matches ? 'dark' : 'light';
        setEffectiveTheme(next);
        document.documentElement.setAttribute('data-theme', next);
        document.body.classList.remove('theme-light', 'theme-dark');
        document.body.classList.add(`theme-${next}`);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const toggleTheme = async () => {
    const themes = ['light', 'dark', 'auto'];
    const currentIndex = themes.indexOf(theme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];

    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);

    try {
      if (maybeSignedIn()) {
        const userResponse = await api.get('/core/me');
        const userId = userResponse.data.user?.id;

        if (userId) {
          await api.patch(`/v1/users/${userId}/`, {
            theme_preference: toApiThemePreference(nextTheme),
          });
        }
      }
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  const changeTheme = async (newTheme) => {
    const normalized = normalizeUserTheme(newTheme);
    setTheme(normalized);
    localStorage.setItem('theme', normalized);

    try {
      if (maybeSignedIn()) {
        const userResponse = await api.get('/core/me');
        const userId = userResponse.data.user?.id;

        if (userId) {
          await api.patch(`/v1/users/${userId}/`, {
            theme_preference: toApiThemePreference(normalized),
          });
        }
      }
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  const value = {
    theme,
    effectiveTheme,
    toggleTheme,
    changeTheme,
    loading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
